import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { extend, useFrame, useThree } from "@react-three/fiber";

import * as THREE from "three";
import { SpaceObjectType } from "@graphic/types";
import { useObjectGroup } from "@graphic/hooks/graphic/use-object-group";
import { useCreateNameLabel } from "@graphic/hooks/overlay/use-create-name-label";
import { useCreateCone } from "@graphic/hooks/graphic/use-create-cone";
import Geometry from "./Geometry";
import Curve from "./Curve";
import { RENDER_ORDER } from "@graphic/constants";
import { getColorByType, getGeodeticDataFromSatrec } from "@graphic/utils";
import DetailLabel from "./DetailLabel";
import { useLabelControl } from "@graphic/hooks/graphic/use-label-control";
import { useTimeStore } from "@graphic/stores";
import { RADIUS } from "@graphic/constants";
import { malligyongIds } from "@graphic/constants/sats"

extend({ DetailLabel });

export const SpaceObject = ({
  data,
  earthRef,
  defaultLabel
}: {
  data: SpaceObjectType;
  earthRef: React.MutableRefObject<THREE.Object3D<THREE.Object3DEventMap> | null>;
  defaultLabel: string;
}) => {
  const [displayCone, setDisplayCone] = useState(true)

  const {
    groupRef,
    meshRef,
    handleClick,
    handlePointerLeave,
    handlePointerOver,
    getPosition
  } = useObjectGroup(data);
  const { labelRef } = useLabelControl({ data, getPosition });

  const coneRef = useCreateCone(displayCone);

  const get = useThree(state => state.get);

  const direction = new THREE.Vector3();

  useEffect(() => {
    if (!groupRef.current || !labelRef.current || !coneRef.current) return;

    groupRef.current.add(coneRef.current);
    // groupRef.current.add(labelRef.current);

    groupRef.current.layers.enableAll();
    return () => {
      const scene = get().scene;

      groupRef.current && scene.remove(groupRef.current);
      meshRef.current && scene.remove(meshRef.current);
      // labelRef.current && scene.remove(labelRef.current);
      coneRef.current && scene.remove(coneRef.current);
    };
  }, [groupRef, labelRef, coneRef]);

  useFrame((state, delta, frame) => {
    if (!groupRef.current) return;

    if (!earthRef.current || !labelRef.current) return;

    const { camera, raycaster } = state;
    const position = groupRef.current.position;
    const distance = position.distanceTo(new THREE.Vector3());
    if (RADIUS + 2000 < distance && !malligyongIds.includes(data.noradId)) {
      setDisplayCone(false)
    }

    direction.subVectors(camera.position, position).normalize();

    raycaster.set(position, direction);
    const intersects = raycaster.intersectObject(earthRef.current);
    labelRef.current.visible = intersects.length === 0;

    if ((labelRef.current as DetailLabel).status === "expand") {
      const time = useTimeStore.getState().time;

      const { satrec } = data;

      const labelData = getGeodeticDataFromSatrec(satrec, time);
      const bodyText = makeLabelBodyText(labelData);
      (labelRef.current as DetailLabel).setBodyText(bodyText);
    }
  }, RENDER_ORDER.VISIBLE);

  const makeLabelBodyText = useCallback(data => {
    const { latitude, longitude, height, inclination } = data;

    const latitudeLabel = `Latitude: ${latitude.toFixed(2).toString()}°`;
    const longitudeLabel = `Longitude: ${longitude.toFixed(2).toString()}°`;
    const altitudeLabel = `Altitude: ${height.toFixed(2).toString()}km`;
    const inclinationLabel = `Inclination: ${inclination.toFixed(2).toString()}°`;
    const timeLabel = `Time: ${useTimeStore.getState().time.utc().format("YYYY-MM-DD HH:mm:ss")}Z`;
    const bodyText = `${latitudeLabel}\n${longitudeLabel}\n${altitudeLabel}\n${inclinationLabel}\n${timeLabel}`;

    return bodyText;
  }, []);

  const color = useMemo(() => {
    return getColorByType(data.type);
  }, [data.type]);

  return (
    <>
      <group ref={groupRef}>
        <mesh
          ref={meshRef}
          onClick={handleClick}
          onPointerOver={handlePointerOver}
          onPointerLeave={handlePointerLeave}
        >
          <Geometry type={"PAYLOAD"} subtype={data.type} />
        </mesh>
      </group>

      <Curve data={data} type="default" subtype={data.type} />
      <detailLabel
        ref={labelRef as MutableRefObject<DetailLabel>}
        args={[data.name, "right", "rgba(60,60,60, 0.8)", 70, data.noradId, defaultLabel]}
      />
    </>
  );
};

export default SpaceObject;
